/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import type { ListViewProps } from '@airasia-phoenix/react-native/components/ListView';
import TextFieldView from '@airasia-phoenix/react-native/components/TextFieldView';
import type { TextFieldRefs } from '@airasia-phoenix/react-native/components/TextFieldView/types';
import TextView, {
  TextViewProps,
} from '@airasia-phoenix/react-native/components/TextView';
import { getColor, pd } from '@airasia-phoenix/react-native/utils';
import React, { type Ref } from 'react';
import { Pressable } from 'react-native';
import type { LoginViewResolvedProps } from '../../widgets/LoginView/loginViewProps';
import { EmailDomainFieldView } from '../EmailDomainFieldView';

export type EmailLoginFormViewProps = {
  emailFieldProps: LoginViewResolvedProps['emailFieldProps'];
  passwordFieldProps: LoginViewResolvedProps['passwordFieldProps'];
  forgotPasswordProps: TextViewProps;
  email?: string;
  password?: string;
  emailErrorMessage: string;
  passwordErrorMessage: string;
  onEmailChange: (text: string) => void;
  onPasswordChange: (text: string) => void;
  /** False in passwordless mode, where the password field is hidden. */
  showPassword: boolean;
  isSecure: boolean;
  onToggleSecure: () => void;
  onForgotPasswordClick?: () => void;
  emailFieldRef?: Ref<TextFieldRefs>;
  showEmailDomainDropdown?: boolean;
  emailDomainDropdownData?: ListViewProps[];
  emailDomainDropdownHeight?: number;
  onEmailDomainSelect?: (item: ListViewProps) => void;
  onEmailFocusChange?: (focused: boolean) => void;
  emailClearEndIcon?: LoginViewResolvedProps['emailFieldProps']['endIcon'];
  onEmailEndIconPress?: () => void;
};

/** Email and password entry, plus the forgot-password link. */
export function EmailLoginFormView({
  emailFieldProps,
  passwordFieldProps,
  forgotPasswordProps,
  email,
  password,
  emailErrorMessage,
  passwordErrorMessage,
  onEmailChange,
  onPasswordChange,
  showPassword,
  isSecure,
  onToggleSecure,
  onForgotPasswordClick,
  emailFieldRef,
  showEmailDomainDropdown = false,
  emailDomainDropdownData,
  emailDomainDropdownHeight,
  onEmailDomainSelect,
  onEmailFocusChange,
  emailClearEndIcon,
  onEmailEndIconPress,
}: EmailLoginFormViewProps) {
  return (
    <>
      <EmailDomainFieldView
        emailFieldProps={emailFieldProps}
        emailFieldRef={emailFieldRef}
        email={email}
        emailErrorMessage={emailErrorMessage}
        onEmailChange={onEmailChange}
        showEmailDomainDropdown={showEmailDomainDropdown}
        emailDomainDropdownData={emailDomainDropdownData}
        emailDomainDropdownHeight={emailDomainDropdownHeight}
        onEmailDomainSelect={onEmailDomainSelect}
        onEmailFocusChange={onEmailFocusChange}
        emailClearEndIcon={emailClearEndIcon}
        onEmailEndIconPress={onEmailEndIconPress}
      />

      {showPassword && (
        <>
          <TextFieldView
            {...passwordFieldProps}
            testID="password-input"
            value={password}
            isError={!!passwordErrorMessage}
            errorMessage={passwordErrorMessage}
            onChangeText={onPasswordChange}
            secureTextEntry={isSecure}
            disabled={passwordFieldProps.disabled ?? false}
            endIcon={{
              icon: isSecure
                ? pd.icon.main.actions.visibility_off
                : pd.icon.main.actions.visibility,
              height: 24,
              width: 24,
              tint: getColor(pd.sys.colour.content_secondary),
            }}
            onEndIconPress={onToggleSecure}
          />
          <Pressable onPress={onForgotPasswordClick}>
            <TextView {...forgotPasswordProps} testID="forgot-password" />
          </Pressable>
        </>
      )}
    </>
  );
}
